/**
 * Unified notification system (Singleton)
 */
export class NotificationManager {
  constructor() {
    // Implement singleton pattern
    if (NotificationManager.instance) {
      return NotificationManager.instance;
    }

    this.elements = {
      mainNotification: document.querySelector(".main-notification"),
      resetNotification: document.querySelector(".reset-items-notification"),
      notificationContent: document.querySelector(".notification-content"),
      resetContent: document.querySelector(
        ".reset-items-notification .notification-content"
      ),
      resetConfirm: document.querySelector(".reset-items-confirm"),
      resetCancel: document.querySelector(".reset-items-cancel"),
    };

    NotificationManager.instance = this;
    return this;
  }

  /**
   * Show main notification with message
   * @param {string} message - Message to display
   */
  showMainNotification(message) {
    if (this.elements.mainNotification && this.elements.notificationContent) {
      this.elements.notificationContent.textContent = message;
      this.elements.mainNotification.classList.add("active");
    }
  }

  /**
   * Hide main notification
   */
  hideMainNotification() {
    if (this.elements.mainNotification) {
      this.elements.mainNotification.classList.remove("active");
    }
  }

  /**
   * Show confirmation dialog for clearing selections
   * @param {string} message - Confirmation message
   * @param {Function} onConfirm - Callback when user confirms
   * @param {Function} onCancel - Callback when user cancels (optional)
   */
  showResetConfirmation(message, onConfirm, onCancel = null) {
    if (!this.elements.resetNotification || !this.elements.resetContent) {
      console.warn("Reset notification elements not found");
      return;
    }

    // Set message
    this.elements.resetContent.textContent = message;

    // Show dialog
    this.elements.resetNotification.classList.add("active");

    // Store current callbacks
    this.currentCallbacks = { onConfirm, onCancel };

    // Set up one-time event listeners (remove any existing ones first)
    this.removeResetEventListeners();
    this.addResetEventListeners();
  }

  removeResetEventListeners() {
    if (this.confirmHandler) {
      this.elements.resetConfirm?.removeEventListener(
        "click",
        this.confirmHandler
      );
    }
    if (this.cancelHandler) {
      this.elements.resetCancel?.removeEventListener(
        "click",
        this.cancelHandler
      );
    }
  }

  addResetEventListeners() {
    this.confirmHandler = () => {
      this.hideResetConfirmation();
      if (this.currentCallbacks?.onConfirm) {
        this.currentCallbacks.onConfirm();
      }
    };

    this.cancelHandler = () => {
      this.hideResetConfirmation();
      if (this.currentCallbacks?.onCancel) {
        this.currentCallbacks.onCancel();
      }
    };

    if (this.elements.resetConfirm) {
      this.elements.resetConfirm.addEventListener("click", this.confirmHandler);
    }

    if (this.elements.resetCancel) {
      this.elements.resetCancel.addEventListener("click", this.cancelHandler);
    }
  }

  /**
   * Hide reset confirmation dialog
   */
  hideResetConfirmation() {
    if (this.elements.resetNotification) {
      this.elements.resetNotification.classList.remove("active");
    }
  }

  /**
   * Show success message with timestamp
   * @param {string} actionType - Type of action (e.g., "confirmed", "reset")
   * @param {string} itemType - Type of item (e.g., "interior", "plan", "extras")
   * @param {string} timestamp - Timestamp of action
   */
  showSuccessMessage(actionType, itemType, timestamp) {
    let message;

    switch (actionType) {
      case "confirmed":
        switch (itemType) {
          case "interior":
            message = `Siseviimistlus on kinnitatud ${timestamp}`;
            break;
          case "plan":
            message = `Planeering on kinnitatud ${timestamp}`;
            break;
          case "furniture":
            message = `Päring saadetud ${timestamp}`;
            break;
          case "extras":
            message = `Lisad on kinnitatud ${timestamp}`;
            break;
          default:
            message = `${itemType} kinnitatud ${timestamp}`;
        }
        break;

      case "reset":
        switch (itemType) {
          case "interior":
            message = "Siseviimistluse kinnitus on tühistatud";
            break;
          case "plan":
            message = "Planeeringu kinnitus on tühistatud";
            break;
          case "furniture":
            message = "Päring on tühistatud";
            break;
          case "extras":
            message = "Lisade kinnitus on tühistatud";
            break;
          default:
            message = `${itemType} kinnitus on tühistatud`;
        }
        break;

      default:
        message = `${actionType} ${itemType}`;
    }

    this.showMainNotification(message);
  }
}

// Static property for singleton
NotificationManager.instance = null;
